import { Routes, RouterModule } from "@angular/router";
import { NgModule } from "@angular/core";
import { AuthGuard } from "../@core/auth/auth.guard";
import { WordListComponent } from "./core/components/word-list/word-list.component";
import { RestartServiceComponent } from "./core/components/restart-service/restart-service.component";
import { RoomsListComponent } from "./core/components/rooms-list/rooms-list.component";
import { WarehouseListComponent } from "./core/components/warehouse-list/warehouse-list.component";
import { RoleGuard } from "../@core/auth/auth.roleguard.service";
import { VarietiesListComponent } from "./core/components/varieties/varieties-list/varieties-list.component";
import { VarietiesDetailComponent } from "./core/components/varieties/varieties-detail/varieties-detail.component";
import { CarrierListComponent } from "./core/components/carrier-list/carrier-list.component";
import { TruckTypeListComponent } from "./core/components/truck-type-list/truck-type-list.component";
import { FarmDetailComponent } from "./core/components/farms/farm-detail/farm-detail.component";
import { FarmsListComponent } from "./core/components/farms/farms-list/farms-list.component";
import { PoolListComponent } from "./core/components/pool-list/pool-list.component";
import { StorageListComponent } from "./core/components/storage-list/storage-list.component";
import { FieldmanListComponent } from "./core/components/fieldman-list/fieldman-list.component";
import { EstimatesLockComponent } from "./core/components/estimates-lock/estimates-lock.component";
import { PackageTypesComponent } from "./core/components/package-types/package-types.component";
import { RoomLocationsComponent } from "./core/components/room-locations/room-locations.component";
import { LocationBarcodeListComponent } from "./core/components/location-barcode-list/location-barcode-list.component";
import { ContractInputComponent } from "./modules/contracts/contract-input/contract-input.component";
import { ContractListComponent } from "./modules/contracts/contract-list/contract-list.component";
import { ContractAmmendComponent } from "./modules/contracts/contract-ammend/contract-ammend.component";
import { EmployeesComponent } from "./core/components/employees/employees.component";
import { EmployeeEditComponent } from "./core/components/employees/employee-edit/employee-edit.component";
import { CropListComponent } from "./core/components/crop-list/crop-list.component";
import { RoomDashboardComponent } from "./core/components/rooms-list/room-dashboard/room-dashboard.component";
import { DownloadsComponent } from "./core/components/downloads/downloads.component";
import { OperationOverviewComponent } from "./core/components/operation-overview/operation-overview.component";

const routes: Routes = [
    {
        path: 'estimates',
        loadChildren: () => import('app/harvest-engine/modules/estimates/estimates.module').then(m => m.EstimatesModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'work-orders',
        loadChildren: () => import('app/harvest-engine/modules/work-orders/work-orders.module').then(m => m.WorkOrdersModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'task-work-orders',
        loadChildren: () => import('app/harvest-engine/modules/task-work-orders/task-work-orders.module').then(m => m.TaskWorkOrdersModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'field-qc',
        loadChildren: () => import('app/harvest-engine/modules/field-qc/field-qc.module').then(m => m.FieldQcModule),
        canActivate: [AuthGuard]
    },

    {
        path: 'web-store',
        loadChildren: () => import('app/harvest-engine/modules/web-store/web-store.module').then(m => m.WebStoreModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'crm',
        loadChildren: () => import('app/harvest-engine/modules/crm/crm.module').then(m => m.CrmModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'laboratory',
        loadChildren: () => import('app/harvest-engine/modules/laboratory/laboratory.module').then(m => m.LaboratoryModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'pick-plan',
        loadChildren: () => import('app/harvest-engine/modules/pick-plan/pick-plan.module').then(m => m.PickPlanModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'inventory',
        loadChildren: () => import('app/harvest-engine/modules/inventory/inventory.module').then(m => m.InventoryModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'quality-control',
        loadChildren: () => import('app/harvest-engine/modules/quality-control/quality-control.module').then(m => m.QualityControlModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'integrations',
        loadChildren: () => import('app/harvest-engine/modules/integrations/integrations.module').then(m => m.IntegrationsModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'scouting',
        loadChildren: () => import('app/harvest-engine/modules/field-events/field-events.module').then(m => m.FieldEventsModule)
    },
    {
        path: 'spray',
        loadChildren: () => import('app/harvest-engine/modules/spray/spray.module').then(m => m.SprayModule)
    },
    {
        path: 'financial',
        loadChildren: () => import('app/harvest-engine/modules/financial/financial.module').then(m => m.FinancialModule)
    },
    {
        path: 'hop-analysis',
        loadChildren: () => import('app/harvest-engine/modules/hop-analysis/hop-analysis.module').then(m => m.HopAnalysisModule)
    },
    {
        path: 'farm-assessment',
        loadChildren: () => import('app/harvest-engine/modules/farm-assessment/farm-assessment.module').then(m => m.FarmAssessmentModule),
        canActivate: [AuthGuard],
    },
    {
        path: 'purchasing',
        loadChildren: () => import('app/harvest-engine/modules/purchase-order/purchase-order.module').then(m => m.PurchaseOrderModule),

        canActivate: [AuthGuard],
    },
    {
        path: 'contracts/contract-input',
        component: ContractInputComponent,
    },
    {
        path: 'contracts/contract-list',
        component: ContractListComponent,
    },
    {
        path: 'contracts/contract-ammend',
        component: ContractAmmendComponent,
    },
    {
        path: 'core/word-list',
        component: WordListComponent,
        canActivate: [RoleGuard, AuthGuard],
        data: {
            expectedRole: ['admin']
        }
    },

    {
        path: 'core/estimates-lock',
        component: EstimatesLockComponent,
    },
    {
        path: 'core/restart-service',
        component: RestartServiceComponent,
        canActivate: [RoleGuard, AuthGuard],
        data: {
            expectedRole: ['kw-admin']
        }
    },
    {
        path: 'core/rooms-list',
        component: RoomsListComponent,
        canActivate: [RoleGuard, AuthGuard],
        data: {
            expectedRole: ['admin']
        }
    },
    {
        path: 'core/room-dashboard/:id',
        component: RoomDashboardComponent,
        canActivate: [AuthGuard],
    },
    {
        path: 'core/warehouse-list',
        component: WarehouseListComponent,
        canActivate: [RoleGuard, AuthGuard],
        data: {
            expectedRole: ['admin']
        }
    },

    {
        path: 'core/varieties',
        // loadChildren: 'app/harvest-engine/core/components/varieties/varieties.module#VarietiesModule',
        component: VarietiesListComponent,
        canActivate: [RoleGuard, AuthGuard],
        data: {
            expectedRole: ['admin']
        }
    },
    {
        path: 'core/varieties/detail',
        component: VarietiesDetailComponent,
        canActivate: [RoleGuard, AuthGuard],
        data: {
            expectedRole: ['admin']
        }
    },
    {
        path: 'core/varieties/detail/:id',
        component: VarietiesDetailComponent,
        canActivate: [RoleGuard, AuthGuard],
        data: {
            expectedRole: ['admin']
        }
    },
    {

        path: 'core/farms',
        component: FarmsListComponent,
        canActivate: [RoleGuard, AuthGuard],
        data: {
            expectedRole: ['admin', 'edit:farms', 'read:farms']
        }
    },
    {
        path: 'core/operation-overview',
        component: OperationOverviewComponent,
        canActivate: [RoleGuard, AuthGuard],
        data: {
            expectedRole: ['admin', 'edit:farms', 'read:farms']
        }
    },
    {
        path: 'core/operation-overview/farm',
        loadChildren: () => import('app/harvest-engine/core/components/operation-overview/operation-farm.module').then(m => m.OperationFarmModule),
        canActivate: [RoleGuard, AuthGuard],
        data: {
            expectedRole: ['admin', 'edit:farms', 'read:farms']
        }
    },
    {
        path: 'core/carrier-list',
        component: CarrierListComponent,
    },
    {
        path: 'core/farms/lifecycle',
        loadChildren: () => import('app/harvest-engine/core/components/farms/farm-lifecycle.module').then(m => m.FarmLifecycleModule),
        canActivate: [RoleGuard, AuthGuard],
        data: {
            expectedRole: ['admin', 'edit:farms', 'read:farms']
        }
    },
    {

        path: 'core/farms/detail',
        component: FarmDetailComponent,
        canActivate: [RoleGuard, AuthGuard],
        data: {
            expectedRole: ['admin', 'edit:farms', 'read:farms']
        }
    },
    {
        path: 'core/truck-type-list',
        component: TruckTypeListComponent,
    },
    {
        path: 'core/farms/detail/:id',
        component: FarmDetailComponent,
        canActivate: [RoleGuard, AuthGuard],
        data: {
            expectedRole: ['admin', 'edit:farms', 'read:farms']
        }
    },

    {
        path: 'core/package-types',
        component: PackageTypesComponent,
        canActivate: [RoleGuard, AuthGuard],
        data: {
            expectedRole: ['admin']
        }
    },
    {
        path: 'core/blocks',
        loadChildren: () => import('app/harvest-engine/core/components/blocks/blocks.module').then(m => m.BlocksModule),
        canActivate: [RoleGuard, AuthGuard],
        data: {
            expectedRole: ['read:farms']
        }
    },
    {
        path: 'core/room-locations',
        component: RoomLocationsComponent,
        canActivate: [RoleGuard, AuthGuard],
        data: {
            expectedRole: ['admin']
        }
    },
    {
        path: 'core/location-barcodes',
        component: LocationBarcodeListComponent,
        canActivate: [RoleGuard, AuthGuard],
        data: {
            expectedRole: ['admin']
        }
    },
    {
        path: 'core/pool-list',
        component: PoolListComponent,
        canActivate: [RoleGuard, AuthGuard],
        data: {
            expectedRole: ['admin']
        }
    },
    {
        path: 'core/crop-list',
        component: CropListComponent,
        canActivate: [RoleGuard, AuthGuard],
        data: {
            expectedRole: ['admin']
        }
    },
    {
        path: 'core/storage-list',
        component: StorageListComponent,
        canActivate: [RoleGuard, AuthGuard],
        data: {
            expectedRole: ['admin']
        }
    },
    {
        path: 'core/fieldman-list',
        component: FieldmanListComponent,
        canActivate: [RoleGuard, AuthGuard],
        data: {
            expectedRole: ['admin']
        }
    },
    {
        path: 'farm-features',
        loadChildren: () => import('app/harvest-engine/modules/farm-features/farm-feature.module').then(m => m.FarmFeatureModule),
        canActivate: [AuthGuard]
    },
    // // // {
    // // //     path: 'overview-map',
    // // //     loadChildren: () => import('app/harvest-engine/modules/overview-map/overview-map.module').then(m => m.OverviewMapModule),
    // // //     canActivate: [AuthGuard]
    // // // },
    {
        path: 'scales',
        loadChildren: () => import('app/harvest-engine/modules/scale/scale.module').then(m => m.ScaleModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'chat',
        loadChildren: () => import('app/harvest-engine-ai/harvest-engine-ai.module').then(m => m.HarvestEngineAiModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'core/employee-list',
        component: EmployeesComponent,
    },
    {
        path: 'core/employee-edit',
        component: EmployeeEditComponent,
    },
    {
        path: 'core/downloads',
        component: DownloadsComponent,
    }
];


@NgModule({
    imports: [RouterModule.forChild(routes)],
    exports: [RouterModule],
})
export class HarvestEngineRoutingModule {
}